import { ChevronRight, ExpandMore } from '@mui/icons-material'
import React, { useState } from 'react'
import styled from 'styled-components'

interface Filter {
  field: string
  values?: (string | number)[] | NestedValue[]
  min?: number | string
  max?: number | string
  /**
   * How the field compares — `is` (the default) or `is_not`.
   *
   * Absent on every filter that is not a value selection, and omitted by the
   * backend when it is `is`, so the negated case is the only one that arrives.
   * Without rendering it, a chart filtered to everything EXCEPT one brand
   * showed "Brand: Acme" — stating the exact opposite of what the chart draws.
   */
  operator?: string
  /**
   * A threshold's comparison value — the `value` half of `operator`/`value`.
   *
   * Present only on a metric-threshold chip, which carries no `values`, `min`
   * or `max` and so matched none of the branches below: it rendered as
   * "Filter applied", dropping the comparison, the number and the note, and
   * sitting in the list as though it were an ordinary record filter.
   */
  value?: number | string
  /**
   * How many values an axis selects when its names could not be resolved.
   *
   * A theme filter over deleted or hidden clusters, or over a topic with no
   * parent, has no names to show — but the axis IS filtered, and a chip that
   * said nothing rendered as "Filter applied", which reads as a filter with no
   * content rather than as names that are missing.
   */
  total_selected?: number
  /**
   * Why this chip needs explaining, in the backend's own words.
   *
   * A threshold drops whole breakdown values rather than filtering records; an
   * unresolvable theme axis is filtered by ids whose names are gone. Both are
   * readings a bare label invites and the note exists to prevent.
   */
  note?: string
}

interface NestedValue {
  field: string
  values: string[]
}

interface FilterDisplayProps {
  filters: Filter[]
}

const FilterDisplay: React.FC<FilterDisplayProps> = ({ filters }) => {
  const [expandedThemes, setExpandedThemes] = useState<Set<string>>(new Set())

  const toggleTheme = (themeName: string): void => {
    setExpandedThemes((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(themeName)) {
        newSet.delete(themeName)
      } else {
        newSet.add(themeName)
      }
      return newSet
    })
  }
  const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp * 1000)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    })
  }

  const formatTimestamp = (timestamp: number | string): string => {
    if (typeof timestamp === 'string') {
      return timestamp
    }
    return formatDate(timestamp)
  }

  const isNestedValues = (values: unknown): values is NestedValue[] => {
    return (
      Array.isArray(values) &&
      values.length > 0 &&
      typeof values[0] === 'object' &&
      values[0] !== null &&
      'field' in values[0] &&
      'values' in values[0]
    )
  }

  const isNegated = (operator?: string): boolean =>
    operator != null && operator !== 'is'

  // The label carries the negation rather than the value badges: a reader
  // scans the field name first, and marking N badges says the same thing N
  // times while leaving the field itself ambiguous.
  const label = (field: string, operator?: string): JSX.Element => (
    <FilterLabel>
      {field}
      {isNegated(operator) && <ExcludedTag>excluded</ExcludedTag>}
    </FilterLabel>
  )

  // Keyed on the backend's `NumericOperator` literal, all six of it. This said
  // `neq`, which the backend never emits, and omitted `not_eq`, which it does —
  // so a `not_eq` threshold rendered as the raw text `not_eq 100`. The fallback
  // below is why nothing surfaced it: a mismatch degrades to the operator's own
  // name rather than failing.
  const OPERATOR_SYMBOLS: Record<string, string> = {
    gt: '>',
    gte: '\u2265',
    lt: '<',
    lte: '\u2264',
    eq: '=',
    not_eq: '\u2260',
  }

  const renderFilter = (filter: Filter, index: number): JSX.Element => {
    const { field, values, min, max, operator, value, note, total_selected } =
      filter

    // An axis whose VALUES could not be resolved: filtered, but with no names
    // to list. Without a branch it fell to the unknown-filter arm and rendered
    // "Filter applied" — dropping the count and the note that says why the
    // names are missing, which is the same failure the threshold chip had.
    if (total_selected != null) {
      return (
        <FilterSection key={index}>
          {label(field, operator)}
          <FilterValue>
            {`${total_selected} ${total_selected === 1 ? 'value' : 'values'} selected`}
          </FilterValue>
          {note != null && <FilterNote>{note}</FilterNote>}
        </FilterSection>
      )
    }

    // A threshold: `operator` + `value`, and no values/min/max to match on.
    if (value != null && operator != null) {
      return (
        <FilterSection key={index}>
          <FilterLabel>{field}</FilterLabel>
          <FilterValue>
            {`${OPERATOR_SYMBOLS[operator] ?? operator} ${value}`}
          </FilterValue>
          {note != null && <FilterNote>{note}</FilterNote>}
        </FilterSection>
      )
    }

    // Handle date range
    if (field === 'Date Range' && (min != null || max != null)) {
      let dateRangeText = ''
      if (min != null && max != null) {
        dateRangeText = `${formatTimestamp(min)} - ${formatTimestamp(max)}`
      } else if (min != null) {
        dateRangeText = `From ${formatTimestamp(min)}`
      } else if (max != null) {
        dateRangeText = `Until ${formatTimestamp(max)}`
      }

      return (
        <FilterSection key={index}>
          <FilterLabel>{field}</FilterLabel>
          <FilterValue>{dateRangeText}</FilterValue>
        </FilterSection>
      )
    }

    // Handle range filters (like star rating)
    if (min != null || max != null) {
      let rangeText = ''
      if (min != null && max != null) {
        rangeText = `${min} - ${max}`
      } else if (min != null) {
        rangeText = `${min} and above`
      } else if (max != null) {
        rangeText = `${max} and below`
      }

      return (
        <FilterSection key={index}>
          <FilterLabel>{field}</FilterLabel>
          <FilterValue>{rangeText}</FilterValue>
        </FilterSection>
      )
    }

    // Handle nested values (like Theme with parent/child structure)
    if (values && isNestedValues(values)) {
      return (
        <FilterSection key={index}>
          {label(field, operator)}
          <NestedContainer>
            {values.map((nestedValue, nestedIndex) => {
              const isExpanded = expandedThemes.has(nestedValue.field)
              return (
                <CollapsibleTheme key={nestedIndex}>
                  <ThemeHeader onClick={() => toggleTheme(nestedValue.field)}>
                    <ThemeHeaderContent>
                      <ExpandIcon>
                        {isExpanded ? (
                          <ExpandMore style={{ fontSize: 20 }} />
                        ) : (
                          <ChevronRight style={{ fontSize: 20 }} />
                        )}
                      </ExpandIcon>
                      <ThemeName>{nestedValue.field}</ThemeName>
                      <ThemeCount>({nestedValue.values.length})</ThemeCount>
                    </ThemeHeaderContent>
                  </ThemeHeader>
                  {isExpanded && (
                    <ThemeContent>
                      <ValueContainer>
                        {nestedValue.values.map((value, valueIndex) => (
                          <ValueBadge key={valueIndex}>{value}</ValueBadge>
                        ))}
                      </ValueContainer>
                    </ThemeContent>
                  )}
                </CollapsibleTheme>
              )
            })}
          </NestedContainer>
        </FilterSection>
      )
    }

    // Handle regular values (brand, search terms, etc.)
    if (values && Array.isArray(values)) {
      return (
        <FilterSection key={index}>
          {label(field, operator)}
          <ValueContainer>
            {values.map((value, valueIndex) => (
              <ValueBadge key={valueIndex}>{value}</ValueBadge>
            ))}
          </ValueContainer>
        </FilterSection>
      )
    }

    // Fallback for unknown filter types
    return (
      <FilterSection key={index}>
        <FilterLabel>{field}</FilterLabel>
        <FilterValue>Filter applied</FilterValue>
      </FilterSection>
    )
  }

  if (!filters || filters.length === 0) {
    return <EmptyState>No additional filters applied</EmptyState>
  }

  return <Container>{filters.map(renderFilter)}</Container>
}

export default FilterDisplay

const Container = styled.div`
  border-radius: 6px;
  font-size: 14px;
  color: #495057;
  overflow-y: auto;
`

const FilterSection = styled.div`
  margin-bottom: 20px;
`

const FilterLabel = styled.div`
  font-weight: 600;
  padding-bottom: 8px;
  color: #28364f;
`

const ExcludedTag = styled.span`
  display: inline-block;
  margin-left: 8px;
  padding: 1px 8px;
  background-color: #fdecea;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #a4262c;
  vertical-align: middle;
`

const FilterValue = styled.div`
  color: #495057;
`

const FilterNote = styled.div`
  margin-top: 4px;
  font-size: 12px;
  font-style: italic;
  color: #6c757d;
`

const ValueContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const ValueBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  background-color: #eeeff3;
  border-radius: 6px;
  font-size: 13px;
  color: #28364f;
  white-space: nowrap;
`

const NestedContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const CollapsibleTheme = styled.div`
  border: 1px solid #e9ecef;
  border-radius: 6px;
  overflow: hidden;
  background-color: #ffffff;
`

const ThemeHeader = styled.div`
  padding: 12px;
  cursor: pointer;
  background-color: #f8f9fa;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #e9ecef;
  }
`

const ThemeHeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ExpandIcon = styled.div`
  display: flex;
  align-items: center;
  color: #495057;
`

const ThemeName = styled.div`
  font-weight: 500;
  color: #28364f;
  font-size: 14px;
`

const ThemeCount = styled.div`
  font-size: 13px;
  color: #6c757d;
  font-weight: 400;
`

const ThemeContent = styled.div`
  padding: 12px;
  background-color: #ffffff;
  border-top: 1px solid #e9ecef;
`

const EmptyState = styled.div`
  font-style: italic;
  color: #666;
  padding: 12px 0;
`
